/**
 * RFC-108-G alternate-group switch event (Phase 2).
 *
 * Fired on `window` whenever the operator switches the active alternate of
 * an RFC-108-G group — either via the LayerPanel dropdown, the SceneTreeV2
 * row toggle, or programmatically through `useViewerStore.setVisible` on a
 * group member.
 *
 * Listeners (today: TwinShell) translate the event into a 400 ms engine
 * crossfade and an `setLayerVisible(false)` on the outgoing alternate.
 *
 * The event is intentionally a `CustomEvent` (browser standard) rather than
 * a Zustand subscription so non-React listeners (engine adapters in
 * particular) can subscribe without holding a React ref.
 */

export const ALTERNATE_SWITCH_EVENT = 'viewer:alternate-switch';

export interface AlternateSwitchEventDetail {
  /** Stable group key from `alternateGroupModel.alternateGroupKey`. */
  groupKey: string;
  /** Previously active member's layer id (`null` if the group was off). */
  fromLayerId: string | null;
  /** Newly active member's layer id. */
  toLayerId: string;
  /**
   * `true` when the group is becoming visible as a side effect of this
   * switch (e.g. the operator turned the group on AND picked a different
   * member from the dropdown in one action). Listeners that pre-fade
   * incoming materials use this to skip the fade-in when the engine is
   * about to start streaming tiles in cold.
   */
  groupBecomingVisible: boolean;
}

export type AlternateSwitchEvent = CustomEvent<AlternateSwitchEventDetail>;

export function emitAlternateSwitch(detail: AlternateSwitchEventDetail): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(ALTERNATE_SWITCH_EVENT, { detail }));
}
